import { projectId, publicAnonKey } from './info';
import { getSession } from './auth';
import { User, Equipment, BorrowRequest, SportsRegistrationSettings } from '../../types';

const API_BASE_URL = `https://${projectId}.supabase.co/functions/v1/make-server-653872f3`;
const inflightGetRequests = new Map<string, Promise<any>>();

// Helper function to get stored access token
export function getAccessToken(): string | null {
  return localStorage.getItem('access_token');
}

// Helper function to set access token
export function setAccessToken(token: string) {
  localStorage.setItem('access_token', token);
}

// Helper function to clear access token
export function clearAccessToken() {
  localStorage.removeItem('access_token');
}

// Helper function to make authenticated requests with auto-retry on token expiration
async function fetchAPI(endpoint: string, options: RequestInit = {}, retryCount = 0): Promise<any> {
  const method = (options.method ?? 'GET').toUpperCase();
  const requestKey = `${method}:${endpoint}`;

  if (method === 'GET' && retryCount === 0) {
    const inflightRequest = inflightGetRequests.get(requestKey);
    if (inflightRequest) {
      return inflightRequest;
    }
  }

  const requestPromise = fetchAPIInternal(endpoint, options, retryCount);

  if (method === 'GET' && retryCount === 0) {
    inflightGetRequests.set(requestKey, requestPromise);
    requestPromise.finally(() => {
      inflightGetRequests.delete(requestKey);
    });
  }

  return requestPromise;
}

async function fetchAPIInternal(endpoint: string, options: RequestInit = {}, retryCount = 0): Promise<any> {
  const token = getAccessToken();
  
  // Only log error if we've already retried (meaning this is a real auth issue)
  // On initial calls without token, we'll just use anon key silently
  if (!token && retryCount > 0) {
    console.error('❌ No access token available for API request to:', endpoint);
    console.error('   This usually means the user is not logged in or the session expired');
  }
  
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  // Use access token if available, otherwise use anon key
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  } else {
    headers['Authorization'] = `Bearer ${publicAnonKey}`;
  }
  
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    let errorData: any;
    let errorMessage: string;
    
    try {
      errorData = await response.json();
      errorMessage = errorData.error || errorData.message || `HTTP ${response.status}`;
    } catch (e) {
      // If response is not JSON, try to get text
      try {
        const textError = await response.text();
        errorMessage = textError || `HTTP ${response.status}: ${response.statusText}`;
      } catch {
        errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      }
    }
    
    // If we get a 401 and haven't retried yet, try refreshing the token
    if (response.status === 401 && retryCount === 0) {
      const { data: sessionData } = await getSession();
      
      if (sessionData?.session?.access_token) {
        setAccessToken(sessionData.session.access_token);
        // Retry the request once with the new token
        return fetchAPIInternal(endpoint, options, retryCount + 1);
      }
    }
    
    throw new Error(errorMessage);
  }

  return response.json();
}

// ==================== AUTH API ====================

export async function signup(data: {
  email: string;
  password: string;
  name: string;
  role: 'admin' | 'coach' | 'faculty' | 'student';
  department?: string;
  studentId?: string;
  assignedSport?: string;
  skipStudentDataBankValidation?: boolean;
}) {
  return fetchAPI('/auth/signup', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getCurrentUser(): Promise<{ user: User }> {
  return fetchAPI('/auth/me');
}

export async function checkAdminSetup(): Promise<{ adminExists: boolean; message: string }> {
  const response = await fetch(`${API_BASE_URL}/admin-setup`, {
    headers: {
      'Authorization': `Bearer ${publicAnonKey}`,
    },
  });
  return response.json();
}

export async function forceAdminSetup(): Promise<{ message: string }> {
  const response = await fetch(`${API_BASE_URL}/admin-setup/force`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${publicAnonKey}`,
    },
  });
  return response.json();
}

// ==================== EQUIPMENT API ====================

export async function getEquipment(): Promise<{ equipment: Equipment[] }> {
  return fetchAPI('/equipment');
}

export async function getEquipmentById(id: string): Promise<{ equipment: Equipment }> {
  return fetchAPI(`/equipment/${id}`);
}

export async function createEquipment(data: Omit<Equipment, 'id' | 'available'>): Promise<{ equipment: Equipment }> {
  return fetchAPI('/equipment', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateEquipment(id: string, data: Partial<Equipment>): Promise<{ equipment: Equipment }> {
  return fetchAPI(`/equipment/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteEquipment(id: string): Promise<{ message: string }> {
  return fetchAPI(`/equipment/${id}`, {
    method: 'DELETE',
  });
}

// ==================== BORROW REQUEST API ====================

export async function getRequests(): Promise<{ requests: BorrowRequest[] }> {
  return fetchAPI('/requests');
}

export async function getRequestById(id: string): Promise<{ request: BorrowRequest }> {
  return fetchAPI(`/requests/${id}`);
}

export async function createRequest(data: {
  equipmentId: string;
  quantity: number;
  borrowDate: string;
  purpose: string;
}): Promise<{ request: BorrowRequest }> {
  return fetchAPI('/requests', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateRequest(
  id: string,
  data: { status: string; returnDate?: string; dueDate?: string }
): Promise<{ request: BorrowRequest }> {
  return fetchAPI(`/requests/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// ==================== USER MANAGEMENT API ====================

export async function getUsers(): Promise<{ users: User[] }> {
  return fetchAPI('/users');
}

export async function updateUser(id: string, data: Partial<User>): Promise<{ user: User }> {
  return fetchAPI(`/users/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function verifyCoach(id: string): Promise<{ user: User; emailSent: boolean; emailError?: string }> {
  return fetchAPI(`/users/${id}/verify-coach`, {
    method: 'POST',
  });
}

export async function deleteUser(id: string): Promise<{ message: string }> {
  return fetchAPI(`/users/${id}`, {
    method: 'DELETE',
  });
}

// ==================== TRANSACTION HISTORY API ====================

export interface Transaction {
  id: string;
  requestId: string;
  equipmentId: string;
  equipmentName: string;
  userId: string;
  userName: string;
  action: string;
  timestamp: string;
  details: string;
}

export async function getTransactions(): Promise<{ transactions: Transaction[] }> {
  return fetchAPI('/transactions');
}

// ==================== STATISTICS API ====================

export interface DashboardStats {
  totalEquipment: number;
  totalUsers: number;
  pendingRequests: number;
  activeLoans: number;
  totalAvailable: number;
}

export async function getStats(): Promise<{ stats: DashboardStats }> {
  return fetchAPI('/stats');
}

export interface TransactionStats {
  total: number;
  today: number;
  thisWeek: number;
  thisMonth: number;
  thisYear: number;
  byAction: Record<string, number>;
  byMonth: Array<{ month: number; monthName: string; count: number }>;
  byDay: Array<{ day: number; count: number }>;
  recentTransactions: Transaction[];
  filters: {
    year: string | null;
    month: string | null;
    day: string | null;
  };
}

export async function getTransactionStats(filters?: {
  year?: string;
  month?: string;
  day?: string;
}): Promise<{ stats: TransactionStats }> {
  const queryParams = new URLSearchParams();
  if (filters?.year) queryParams.append('year', filters.year);
  if (filters?.month) queryParams.append('month', filters.month);
  if (filters?.day) queryParams.append('day', filters.day);
  
  const endpoint = `/stats/transactions${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
  return fetchAPI(endpoint);
}

// ==================== DOCUMENT MANAGEMENT API ====================

export interface DocumentMetadata {
  name: string;
  category: string;
  fileType: string;
  fileSize: number;
}

export async function uploadDocument(file: File, category: string): Promise<{ document: any }> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('category', category);

  const token = getAccessToken();
  
  const headers: HeadersInit = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  } else {
    headers['Authorization'] = `Bearer ${publicAnonKey}`;
  }

  const response = await fetch(`${API_BASE_URL}/documents/upload`, {
    method: 'POST',
    headers,
    body: formData,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
  }

  return response.json();
}

export async function getDocuments(): Promise<{ documents: any[] }> {
  return fetchAPI('/documents');
}

export async function deleteDocument(id: string): Promise<void> {
  await fetchAPI(`/documents/${id}`, {
    method: 'DELETE',
  });
}

export async function getDocumentDownloadUrl(id: string): Promise<{ url: string }> {
  return fetchAPI(`/documents/${id}/download`);
}

// ==================== NOTIFICATION API ====================

export async function getNotifications(): Promise<{ notifications: any[] }> {
  return fetchAPI('/notifications');
}

export async function markNotificationRead(id: string): Promise<{ notification: any }> {
  return fetchAPI(`/notifications/${id}/read`, {
    method: 'PUT',
  });
}

export async function markAllNotificationsRead(): Promise<{ message: string }> {
  return fetchAPI('/notifications/read-all', {
    method: 'PUT',
  });
}

export async function deleteNotification(id: string): Promise<void> {
  await fetchAPI(`/notifications/${id}`, {
    method: 'DELETE',
  });
}

// ==================== PROFILE API ====================

export async function updateProfile(data: { 
  name?: string; 
  email?: string;
  department?: string;
  studentId?: string;
}): Promise<{ user: any }> {
  return fetchAPI('/profile', {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function changePassword(data: {
  currentPassword: string;
  newPassword: string;
}): Promise<{ message: string }> {
  return fetchAPI('/profile/password', {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function uploadProfileImage(file: File): Promise<{ user: any }> {
  const formData = new FormData();
  formData.append('file', file);

  const token = getAccessToken();
  
  const headers: HeadersInit = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  } else {
    headers['Authorization'] = `Bearer ${publicAnonKey}`;
  }

  const response = await fetch(`${API_BASE_URL}/profile/image`, {
    method: 'POST',
    headers,
    body: formData,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
  }

  return response.json();
}

// ==================== FEEDBACK API ====================

export async function submitFeedback(data: {
  requestId: string;
  rating: number;
  serviceRating: number;
  comments: string;
  suggestions?: string;
}): Promise<{ feedback: any }> {
  return fetchAPI('/feedback', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getFeedback(): Promise<{ feedback: any[] }> {
  return fetchAPI('/feedback');
}

export async function getFeedbackStats(): Promise<{ stats: any }> {
  return fetchAPI('/feedback/stats');
}

export async function checkRequestHasFeedback(requestId: string): Promise<{ hasFeedback: boolean }> {
  return fetchAPI(`/feedback/check/${requestId}`);
}

export async function getFeedbackByRequest(requestId: string): Promise<{ feedback: any | null }> {
  return fetchAPI(`/feedback/request/${requestId}`);
}

// Batch check feedback for multiple requests in parallel (avoids N+1)
export async function checkFeedbackBatch(requestIds: string[]): Promise<Record<string, { hasFeedback: boolean; feedback: any | null }>> {
  const results: Record<string, { hasFeedback: boolean; feedback: any | null }> = {};
  const checks = await Promise.all(
    requestIds.map(async (id) => {
      try {
        const { hasFeedback } = await checkRequestHasFeedback(id);
        let feedback = null;
        if (hasFeedback) {
          const res = await getFeedbackByRequest(id);
          feedback = res.feedback;
        }
        return { id, hasFeedback, feedback };
      } catch {
        return { id, hasFeedback: false, feedback: null };
      }
    })
  );
  for (const r of checks) {
    results[r.id] = { hasFeedback: r.hasFeedback, feedback: r.feedback };
  }
  return results;
}



// ==================== SPORTS REGISTRATION & EVALUATION API ====================

// --- Sports Categories ---

export async function getSportsCategories(): Promise<{ categories: any[] }> {
  return fetchAPI('/sports/categories');
}

export async function getSportsCategoryBySlug(slug: string): Promise<{ category: any }> {
  return fetchAPI(`/sports/categories/${slug}`);
}

export async function createSportsCategory(data: any): Promise<{ category: any }> {
  return fetchAPI('/sports/categories', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateSportsCategory(id: string, data: any): Promise<{ category: any }> {
  return fetchAPI(`/sports/categories/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function updateRequiredMatches(id: string, requiredMatches: number): Promise<{ category: any }> {
  return fetchAPI(`/sports/categories/${id}/required-matches`, {
    method: 'PUT',
    body: JSON.stringify({ requiredMatches }),
  });
}

export async function deleteSportsCategory(id: string): Promise<{ message: string }> {
  return fetchAPI(`/sports/categories/${id}`, {
    method: 'DELETE',
  });
}

export async function seedDefaultSportsCategories(): Promise<{ message: string; categories: any[] }> {
  return fetchAPI('/sports/categories/seed', {
    method: 'POST',
  });
}

export async function getSportsRegistrationSettings(): Promise<{ settings: SportsRegistrationSettings }> {
  return fetchAPI('/sports/settings/registration-limit');
}

export async function updateSportsRegistrationSettings(data: {
  maxSportsPerStudent: number;
}): Promise<{ settings: SportsRegistrationSettings; message: string }> {
  return fetchAPI('/sports/settings/registration-limit', {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// --- Sport Registrations ---

export async function getSportRegistrations(filters?: {
  sportId?: string;
  status?: string;
  performanceLevel?: string;
}): Promise<{ registrations: any[] }> {
  const params = new URLSearchParams();
  if (filters?.sportId) params.set('sportId', filters.sportId);
  if (filters?.status) params.set('status', filters.status);
  if (filters?.performanceLevel) params.set('performanceLevel', filters.performanceLevel);
  const qs = params.toString();
  return fetchAPI(`/sports/registrations${qs ? `?${qs}` : ''}`);
}

export async function getMySportRegistrations(): Promise<{ registrations: any[] }> {
  return fetchAPI('/sports/registrations/my');
}

export async function createSportRegistration(data: {
  sportId: string;
  membershipType?: string;
  withExperience?: boolean;
  positions?: string[];
  preRankingInfo?: string;
  preRankingScreenshotUrl?: string;
  ign?: string;
  notes?: string;
}): Promise<{ registration: any }> {
  return fetchAPI('/sports/registrations', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateSportRegistration(id: string, data: any): Promise<{ registration: any }> {
  return fetchAPI(`/sports/registrations/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function generateSportRegistrationSummary(id: string): Promise<{ summary: string; registration: any }> {
  return fetchAPI(`/sports/registrations/${id}/generate-summary`, {
    method: 'POST',
  });
}

export async function deleteSportRegistration(id: string): Promise<{ message: string }> {
  return fetchAPI(`/sports/registrations/${id}`, {
    method: 'DELETE',
  });
}

// --- Sport Evaluations ---

export async function getSportEvaluations(registrationId: string): Promise<{ evaluations: any[] }> {
  return fetchAPI(`/sports/evaluations/${registrationId}`);
}

export async function createSportEvaluation(data: {
  registrationId: string;
  scores: Record<string, number | boolean | string>;
  notes?: string;
  position?: string;
}): Promise<{ evaluation: any }> {
  return fetchAPI('/sports/evaluations', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateSportEvaluation(id: string, data: {
  scores?: Record<string, number | boolean | string>;
  notes?: string;
}): Promise<{ evaluation: any }> {
  return fetchAPI(`/sports/evaluations/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteSportEvaluation(id: string): Promise<{ message: string }> {
  return fetchAPI(`/sports/evaluations/${id}`, {
    method: 'DELETE',
  });
}

// --- Match Screenshots (CODM AI Scan) ---

export async function uploadMatchScreenshot(registrationId: string, data: {
  imageUrl: string; // base64 data URL
  ign: string; // player's IGN to search for
}): Promise<{ screenshot: any; registration: any }> {
  return fetchAPI(`/sports/registrations/${registrationId}/match-screenshot`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// --- Sport Statistics ---

export async function getSportStats(): Promise<{ stats: any }> {
  return fetchAPI('/sports/stats');
}

// --- Elimination Rounds ---

export async function getEliminationRounds(sportId: string): Promise<{ rounds: any[] }> {
  return fetchAPI(`/sports/eliminations?sportId=${encodeURIComponent(sportId)}`);
}

export async function createEliminationRound(data: {
  sportId: string;
  name: string;
  roundNumber: number;
  notes?: string;
}): Promise<{ round: any }> {
  return fetchAPI('/sports/eliminations', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateEliminationRound(id: string, data: {
  status?: string;
  name?: string;
  notes?: string;
}): Promise<{ round: any }> {
  return fetchAPI(`/sports/eliminations/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteEliminationRound(id: string): Promise<{ message: string }> {
  return fetchAPI(`/sports/eliminations/${id}`, {
    method: 'DELETE',
  });
}

export async function addEliminationMatch(roundId: string, data: {
  participant1Id: string;
  participant1Name: string;
  participant2Id?: string;
  participant2Name?: string;
  notes?: string;
}): Promise<{ round: any }> {
  return fetchAPI(`/sports/eliminations/${roundId}/matches`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateEliminationMatch(roundId: string, matchId: string, data: {
  score1?: number;
  score2?: number;
  winnerId?: string;
  winnerName?: string;
  status?: string;
  notes?: string;
}): Promise<{ round: any }> {
  return fetchAPI(`/sports/eliminations/${roundId}/matches/${matchId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// ==================== AI SEARCH API ====================

export async function getAIEquipmentSuggestions(
  query: string,
  availableEquipment: Equipment[]
): Promise<{ suggestions: string[]; source?: string }> {
  return fetchAPI('/ai/equipment-search', {
    method: 'POST',
    body: JSON.stringify({
      query,
      availableEquipment,
    }),
  });
}

// ==================== PUBLIC API (no auth required) ====================

// Public: Get all equipment (for landing page, no auth needed)
export async function getPublicEquipment(): Promise<{ equipment: Equipment[] }> {
  const response = await fetch(`${API_BASE_URL}/public/equipment`, {
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${publicAnonKey}`,
    },
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `HTTP ${response.status}`);
  }
  return response.json();
}

// Public: AI equipment search (for landing page, no auth needed)
export async function getPublicAIEquipmentSuggestions(
  query: string,
  availableEquipment: Equipment[]
): Promise<{ suggestions: string[]; source?: string }> {
  const response = await fetch(`${API_BASE_URL}/public/ai/equipment-search`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${publicAnonKey}`,
    },
    body: JSON.stringify({ query, availableEquipment }),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `HTTP ${response.status}`);
  }
  return response.json();
}

// ==================== CHATBOT API ====================

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

export async function sendChatMessage(
  message: string,
  conversationHistory: ChatMessage[]
): Promise<{ response: string }> {
  return fetchAPI('/ai/chat', {
    method: 'POST',
    body: JSON.stringify({
      message,
      conversationHistory: conversationHistory.slice(-10), // Only send last 10 messages for context
    }),
  });
}

// ==================== AI APPLICATION REVIEW API ====================

export async function getAIApplicationReview(
  applicationId: string
): Promise<{ 
  recommendation: 'approve' | 'deny' | 'review_needed';
  confidence: number;
  reasoning: string;
  strengths: string[];
  concerns: string[];
  suggestions: string;
}> {
  return fetchAPI(`/ai/review-application/${applicationId}`, {
    method: 'POST',
  });
}

// ==================== GROQ API KEY MANAGEMENT ====================

export async function setGroqApiKey(apiKey: string): Promise<{ success: boolean }> {
  return fetchAPI('/admin/set-groq-api-key', {
    method: 'POST',
    body: JSON.stringify({ apiKey }),
  });
}

export async function checkGroqApiKey(): Promise<{ configured: boolean }> {
  return fetchAPI('/admin/check-groq-api-key', {
    method: 'GET',
  });
}